import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  addLocalDays,
  epochDayOf,
  isTeacherSide,
  localDateAt,
  localMinutesAt,
  type ScheduleEntry,
  type ScheduleResponse,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { AuthErrorBanner } from "../auth/AuthErrorBanner.tsx";
import { FirstSteps, useEmptyCabinet } from "../shell/FirstSteps.tsx";
import { PartialSessionNote } from "../shell/PartialSessionNote.tsx";
import { TimeZoneDialog } from "../shell/TimeZoneDialog.tsx";
import { zoneLabel, zoneLabelParts, zonesDiffer } from "../../lib/time-zone.ts";
import { CalendarGrid } from "./CalendarGrid.tsx";
import { CreateOccurrenceDialog } from "./CreateOccurrenceDialog.tsx";
import { DropToast } from "./DropToast.tsx";
import type { DropPlan } from "./entry-drag.ts";
import { EntryCard } from "./EntryCard.tsx";
import { EntryChat } from "./EntryChat.tsx";
import { EntryPopover } from "./EntryPopover.tsx";
import { OpeningCard } from "./OpeningCard.tsx";
import {
  gridOpenings,
  BOOK_HINT,
  studentEmptyWeekText,
  weekIsEmpty as isWeekEmpty,
  type GridOpening,
} from "./openings.ts";
import type { CalendarDrag } from "./use-calendar-drag.ts";
import { useScheduleChat } from "./use-schedule-chat.ts";
import { guessTimeZone, mondayOf, occurrenceCountText, rangeTitle, timeText } from "./common.ts";
import { addressWithoutLink, P_DAY, P_LESSON, P_WEEK } from "./lesson-link.ts";
import { useAddressedEntry } from "./use-addressed-entry.ts";
import { useNowScroll } from "./use-now-scroll.ts";
import { SegmentedRadio } from "../../lib/SegmentedRadio.tsx";
import { ChevronIcon } from "../../lib/RowDisclosure.tsx";
import { Loading } from "../../lib/loading.tsx";

/**
 * Раздел «Расписание»: календарь колонками.
 *
 * Раньше здесь был список по дням, один под другим. Календарь колонками (как в
 * Outlook и Teams) добавляет ровно то, чего списку не хватало: видно, где в дне
 * дырка, и видно, что два занятия стоят внахлёст. Из этого же следует и способ
 * создания: по пустому месту можно нажать.
 *
 * Пять решений, ни одно не косметическое:
 *
 * 1. **Неделя и режим живут в адресе** (`?week=`, `?view=`, `?day=`), а не в
 *    `useState`. Расписание на конкретный день пересылается ссылкой, а «назад»
 *    браузера возвращает на прошлый экран, а не выкидывает из раздела
 * 2. **Даты и время считаются в ЗОНЕ СМОТРЯЩЕГО** и только в ней. Занятие может
 *    стоять в чужой зоне (серия принадлежит преподавателю), и раскладка «как в
 *    браузере» дала бы занятие не в том дне у того, кто сидит по другую сторону
 *    полуночи
 * 3. **Состояние занятия рисуется тем, что прислал сервер**, и не
 *    пересчитывается здесь ни на йоту. Часы на устройстве бывают сбиты на
 *    десятки минут, и ученик с убежавшими часами получил бы мёртвую кнопку без
 *    объяснений
 * 4. **Запрос всегда недельный**, в каком бы режиме экран ни был. Листание
 *    внутри недели идёт по данным, которые уже в памяти: три круга по сети на
 *    канале 128 кбит/с человек видит как подвисание, а неделя одного
 *    преподавателя это десятки строк при потолке в пятьсот
 * 5. **Карточка занятия раскрывается РЯДОМ с занятием**, а не внутри колонки и
 *    не под календарём. Внутрь коробки высотой в три строки не влезают ни
 *    кнопка входа, ни причина отмены, ни форма переноса; а под календарём, на
 *    широком экране, теряется связь «нажал здесь, появилось там». Раскладка
 *    карточки живёт в `EntryPopover`, там же и нижнее положение узкого экрана
 * 6. **«Сейчас» идёт по часам СЕРВЕРА.** Отметку текущего времени рисует
 *    календарь, а минуту для неё считает этот экран, и считает её из
 *    `serverNow` со сдвигом на разницу с устройством. Часы на телефоне бывают
 *    сбиты на десятки минут, а полоса «сейчас» это ровно то место, по которому
 *    человек решает, опоздал он или нет
 * 7. **Адрес умеет назвать занятие** (`?lesson=&at=`), и тогда экран открывает
 *    его неделю с уже раскрытой карточкой. Так сюда возвращает выход из комнаты
 *    урока: отметка о приходе, оплата и домашка живут в карточке, и после урока
 *    человек шёл искать её коробку глазами. Разбор в `lesson-link.ts`
 * 8. **Перенос перетаскиванием это ВТОРОЙ вход в ту же ручку**, а не замена
 *    формы. Форма остаётся и не поражается в правах: через неё идёт всё, чего
 *    жест не умеет (клавиатура, телефон, перенос за пределы видимой недели,
 *    перенос с причиной и на минуту, которой нет в сетке). Проект целиком в
 *    `docs/schedule-drag-design.md`, состояние жеста в `use-calendar-drag.ts`
 * 9. **Экран открывается на текущем времени**, а не на первом часе диапазона:
 *    при заходе и по кнопке «Сегодня» окно встаёт так, что полоса «сейчас»
 *    оказывается в верхней четверти видимой области. ⚠️ Просьба о прокрутке
 *    живёт по одной попытке и НЕ выдаётся на каждое перечитывание недели, хотя
 *    перечитывается она после любого действия (отмена, перенос, вход, создание).
 *    Иначе отметка о приходе в карточке вечернего занятия возвращала бы экран
 *    туда, откуда человек только что ушёл своими руками. Хук в `use-now-scroll.ts`
 */

/**
 * Имя режима экрана. Неделя и день объявлены в `lesson-link.ts`: их пишет ещё и
 * уборка ссылки на занятие, а два списка имён разъехались бы молча.
 */
const P_VIEW = "view";

type View = "day" | "days3" | "week";

const VIEW_DAYS: Record<View, number> = { day: 1, days3: 3, week: 7 };

function parseView(value: string | null): View | null {
  return value === "day" || value === "days3" || value === "week" ? value : null;
}

/**
 * Режим по умолчанию от ширины окна.
 *
 * ⚠️ Считается ОДИН раз при первом заходе и в адрес не пишется. Иначе поворот
 * телефона переписывал бы явный выбор человека, а ссылка, присланная с
 * телефона, открывалась бы на десктопе одним днём.
 */
/**
 * Как часто двигается отметка «сейчас».
 *
 * Полминуты, а не секунда: при высоте часа в 3.4rem одна минута это меньше
 * пикселя, и ежесекундная перерисовка недели не даёт человеку ничего, кроме
 * работы для слабого устройства.
 */
const NOW_TICK_MS = 30_000;

/**
 * Часы «сейчас», идущие по времени СЕРВЕРА.
 *
 * ⚠️ Своё время устройства сюда не попадает нигде, кроме как разностью. Сдвиг
 * снимается на каждом ответе расписания (`sync`), и дальше «сейчас» это
 * `Date.now()` плюс этот сдвиг: часы устройства меряют ПРОШЕДШЕЕ время, а не
 * называют текущее. Сбитые на полчаса часы телефона при таком счёте двигают
 * отметку ровно туда, где её видит сервер.
 *
 * Сдвиг живёт в ссылке, а не в состоянии: его правка сама по себе ничего не
 * перерисовывает, перерисовывает тик.
 */
function useServerClock(): { now: number; sync: (serverNow: number) => void } {
  const skew = useRef(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now() + skew.current);
    }, NOW_TICK_MS);
    return () => {
      clearInterval(id);
    };
  }, []);

  const sync = useCallback((serverNow: number) => {
    skew.current = serverNow - Date.now();
    setNow(Date.now() + skew.current);
  }, []);

  return { now, sync };
}

function defaultView(): View {
  const w = typeof window === "undefined" ? 1280 : window.innerWidth;
  if (w < 640) return "day";
  if (w < 1024) return "days3";
  return "week";
}

export function SchedulePage() {
  const { state, adopt } = useSession();
  const [params, setParams] = useSearchParams();

  /**
   * Зона смотрящего: подтверждённая из профиля, иначе догадка браузера.
   *
   * Догадка тут законна ровно потому, что она ничего не записывает: пока
   * человек не нажал в полосе подтверждения, мы просто показываем время в той
   * зоне, в которой он сидит прямо сейчас, и говорим об этом полосой сверху.
   */
  const zone = useMemo(() => {
    const mine = state.status === "signed" ? state.me.timeZone : null;
    return mine ?? guessTimeZone();
  }, [state]);

  /**
   * Ученик расписание только СМОТРИТ.
   *
   * ⚠️ Не потому, что так написан экран: ручки отвечают ему 404 и 409, а
   * `POST /v1/schedule/lessons` берёт преподавателя из сессии и его же паре
   * занятие не заведёт в принципе. Здесь роль читается ровно затем, чтобы не
   * рисовать кнопок, которые отвечают отказом: кнопка, ведущая в отказ, хуже
   * её отсутствия, и это то же правило, по которому у ученика нет кнопки
   * «Подготовить» до начала занятия.
   */
  const canCreate = state.status === "signed" && isTeacherSide(state.me);

  const [fallbackView] = useState(defaultView);
  const view = parseView(params.get(P_VIEW)) ?? fallbackView;

  const { now, sync: syncClock } = useServerClock();
  const todayLocal = localDateAt(now, zone);

  const [data, setData] = useState<ScheduleResponse | null>(null);
  /**
   * Выбранное занятие: его ключ и коробка, по которой нажали.
   *
   * Коробка хранится потому, что от неё считается положение карточки и в неё
   * возвращается фокус при закрытии. Ключ отдельно от неё потому, что после
   * любого действия неделя перечитывается, и строку надо находить заново: сам
   * объект строки к этому моменту уже другой.
   */
  const [selected, setSelected] = useState<{ key: string; anchor: HTMLElement } | null>(null);
  const selectedKey = selected?.key ?? null;
  /** Сетка в DOM: по ней ищется коробка занятия, названного адресом. */
  const gridRef = useRef<HTMLDivElement | null>(null);
  /** Ссылка стабильна намеренно: она уезжает в зависимости эффекта раскрытия. */
  const openEntry = useCallback((key: string, anchor: HTMLElement) => {
    setSelected({ key, anchor });
  }, []);

  /**
   * Занятие, названное адресом: сюда возвращает выход из комнаты урока.
   *
   * ⚠️ Спрашивается ДО недели и до запроса, а не после. Иначе экран сходил бы
   * за текущей неделей, не нашёл в ней занятия и сходил бы второй раз за
   * нужной: два круга по сети на канале 128 кбит/с человек видит как
   * подвисание ровно в ту секунду, когда он вышел с урока.
   */
  const linkDay = useAddressedEntry({
    params,
    setParams,
    zone,
    data,
    grid: gridRef,
    onOpen: openEntry,
  });

  // Неделя занятия перевешивает и параметр адреса, и сегодняшний день: `from`
  // всё равно берёт понедельник, поэтому местной даты занятия тут достаточно
  const week = linkDay ?? params.get(P_WEEK) ?? mondayOf(todayLocal);
  const from = mondayOf(week);
  const to = addLocalDays(from, 6);

  /**
   * Якорь узких режимов: с какого дня недели показывать колонки.
   *
   * Умолчание это сегодня, если он внутри показанной недели, иначе
   * понедельник: открыв прошлую неделю одним днём, человек ждёт её начала, а не
   * пустоты от несуществующей даты.
   */
  // День занятия из адреса перевешивает выбранный: в режимах «день» и «3 дня»
  // без этого показались бы колонки другого дня, и раскрывать было бы нечего
  const anchorParam = linkDay ?? params.get(P_DAY);
  const anchor = useMemo(() => {
    const size = VIEW_DAYS[view];
    const first = epochDayOf(from);
    const wanted =
      anchorParam !== null && anchorParam >= from && anchorParam <= to
        ? anchorParam
        : todayLocal >= from && todayLocal <= to
          ? todayLocal
          : from;
    // Окно не имеет права вылезти за неделю: иначе колонки показывали бы дни,
    // которых сервер в этом ответе не присылал
    const offset = Math.min(epochDayOf(wanted) - first, 7 - size);
    return addLocalDays(from, Math.max(0, offset));
  }, [anchorParam, from, to, todayLocal, view]);

  const dates = useMemo(
    () => Array.from({ length: VIEW_DAYS[view] }, (_, i) => addLocalDays(anchor, i)),
    [anchor, view],
  );

  const [err, setErr] = useState<string | null>(null);
  /**
   * Загрузка ПОВЕРХ уже показанной недели: иначе при каждом переключении
   * экран схлопывался бы в «Загрузка…», и кнопки прыгали бы под курсором на
   * слабом канале.
   */
  const [busy, setBusy] = useState(true);
  const [creating, setCreating] = useState<{ date: string; time: string } | null>(null);
  /** Открыто ли окно смены пояса. Своего адреса у него нет: его не пересылают. */
  const [changingZone, setChangingZone] = useState(false);

  /** Зона, в которой стоят часы устройства прямо сейчас. */
  const browserZone = useMemo(guessTimeZone, []);
  /**
   * Спорят ли часы устройства с записанным поясом.
   *
   * Считает общая `zonesDiffer`: сравнение по поведению, а не по строке, и то
   * же самое сравнение делает окно выбора пояса. Разбор причин у самой функции.
   */
  const confirmedZone = state.status === "signed" ? (state.me.timeZone ?? null) : null;
  /** Неполная сессия: пояс отсюда не меняется (9.6 `docs/account-adopt-design.md`). */
  const partial = state.status === "signed" && state.me.sessionPartial === true;
  const drifted = useMemo(
    () => zonesDiffer(confirmedZone, browserZone),
    [browserZone, confirmedZone],
  );

  /**
   * Перечитывание недели.
   *
   * Отдельной функцией, а не только внутри эффекта, потому что её зовут трое:
   * смена недели, любое действие над занятием (отмена, перенос, вход) и диалог
   * создания. Состояния занятий считает сервер, значит после действия
   * единственный честный способ узнать новое состояние это спросить его заново.
   */
  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.getSchedule({ from, to, tz: zone });
      setData(res);
      // Сдвиг часов снимается на каждом ответе, а не один раз при заходе:
      // вкладку расписания держат открытой днями, и за день устройство успевает
      // уехать, уснуть и проснуться
      syncClock(res.serverNow);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [from, to, syncClock, zone]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Просить ли автопрокрутку к «сейчас» при заходе на экран.
   *
   * ⚠️ Адрес назвал занятие, значит автопрокрутки нет вовсе: `useAddressedEntry`
   * сам доводит коробку занятия до середины экрана и раскрывает карточку, и две
   * прокрутки за один заход спорили бы за одно и то же окно. Занятие сильнее:
   * человек пришёл по ссылке к нему, а не к текущему часу.
   *
   * `useState` с ленивым начальным значением, а не выражением в теле, ради двух
   * мелочей сразу: не дёргать разбор адреса на каждый рендер (а их тут десятки
   * за заход) и не зависеть от того, когда именно `useAddressedEntry` вычеркнет
   * имя занятия. Замком это НЕ является: хук всё равно читает `armed` один раз
   * при монтировании и позже не слушается, так что замков тут два на одной
   * двери, и настоящий стоит в `use-now-scroll.ts`.
   */
  const [armedAtMount] = useState(() => params.get(P_LESSON) === null);
  /**
   * Показанная неделя действительно нарисована: ответ пришёл, он про ЭТУ неделю,
   * и следующий запрос не в полёте. Почему без этого прокрутка промахивается,
   * разобрано в шапке `use-now-scroll.ts`: диапазон часов календаря считается по
   * занятиям.
   *
   * Принцип тот же, что у раскрытия карточки по ссылке: верим границам САМОГО
   * ответа, а не тому, что экран успел о них подумать. Способ при этом другой,
   * `weekCovers` спрашивает про попадание дня в границы, а здесь нужно
   * равенство: неделя показана та же, что запрошена.
   */
  const ready = data !== null && data.from === from && !busy;
  const { ask: askNowScroll, cancel: cancelNowScroll } = useNowScroll({
    grid: gridRef,
    ready,
    armed: armedAtMount,
  });

  /** Правка адреса одной функцией: три параметра связаны и меняются вместе. */
  function go(next: { week?: string; day?: string; view?: View }) {
    /*
      ⚠️ Имя занятия из адреса вычёркивается ЛЮБЫМ листанием, и это не уборка
      мусора, а отмена. Человек взял окно в свои руки, пока запрос был в полёте;
      прыжок на занятие после этого отнял бы у него управление, а неделя ссылки
      перебила бы выбранную им, потому что она сильнее параметра адреса.
    */
    const p = addressWithoutLink(params, null);
    if (next.week !== undefined) p.set(P_WEEK, next.week);
    if (next.day !== undefined) p.set(P_DAY, next.day);
    if (next.view !== undefined) p.set(P_VIEW, next.view);
    // Замена, а не новая запись: три переключения подряд не должны требовать
    // трёх нажатий «назад», чтобы уйти с экрана
    setParams(p, { replace: true });
  }

  /**
   * Шаг листания это размер видимого окна, а не всегда неделя.
   *
   * Человек, смотрящий один день, нажимает «позже» ради завтра. Перескок на
   * неделю тут читается как поломка.
   */
  function shift(direction: -1 | 1) {
    /*
      ⚠️ Автопрокрутка к «сейчас» отменяется ЛИСТАНИЕМ, и отменяется тем же
      доводом, что и имя занятия в адресе: человек взял окно в свои руки, пока
      запрос был в полёте, и прыжок на текущее время после этого отнял бы у него
      управление.

      ⚠️ Стоит это здесь, а не в `go`, хотя `go` зовут трое. Переключатель «День
      / 3 дня / Неделя» вертикаль не трогает вовсе: диапазон часов календаря
      считается по занятиям ВСЕЙ недели и от числа показанных колонок не зависит,
      то есть отменять переключателю нечего, а из `go` он гасил бы просьбу
      захода, и человек, сменивший режим на первой загрузке, остался бы на первом
      часе. Кнопка «Сегодня» просьбу ставит, а не снимает, и зовёт `ask` сама.
    */
    cancelNowScroll();
    const size = VIEW_DAYS[view];
    const target = addLocalDays(anchor, direction * size);
    go({ week: mondayOf(target), day: target });
  }

  const entries = data?.entries ?? [];
  /**
   * Окна записи недели (`docs/booking-design.md`, 3.2): свои и те, на которые
   * можно записаться. Роль считается по окну, поэтому списка два и приезжают
   * они оба любому.
   */
  const openings = useMemo(
    () => gridOpenings(data?.myOpenings ?? [], data?.bookableOpenings ?? []),
    [data],
  );
  /** Неделя без занятий. Пустой она становится, только если нет и окон. */
  const noEntries = !busy && data !== null && entries.length === 0;
  /**
   * Есть окна, где я НЕ преподаватель. Считается по окну, а не по `canCreate`:
   * человек может и учить, и учиться, и подсказка про запись нужна ему в обеих
   * ветках текста пустой недели.
   */
  const canBookHere = openings.some((o) => !o.mine);
  /**
   * Пустая неделя у преподавателя без единого ученика это не «занятий нет», а
   * «кабинет ещё не заведён», и ответ на неё другой. Считается лениво, только
   * когда неделя действительно пуста: разбор в `FirstSteps.tsx`.
   */
  const weekIsEmpty = noEntries && isWeekEmpty(entries.length, openings);
  const { empty: emptyCabinet, recheck: recheckCabinet } = useEmptyCabinet(
    canCreate && weekIsEmpty,
  );
  const selectedEntry = entries.find((e) => e.key === selectedKey) ?? null;
  const selectedOpening = openings.find((o) => o.key === selectedKey) ?? null;
  /**
   * Окно отказало записи: неделю надо перечитать, но не сейчас, а при закрытии
   * карточки. Иначе коробка окна пропала бы вместе с якорем карточки и с
   * объяснением отказа (разбор в `OpeningCard`).
   */
  const staleOpening = useRef(false);
  const closeOpeningCard = useCallback(() => {
    setSelected(null);
  }, []);
  /*
    ⚠️ Точка перечитывания ОДНА и смотрит на смену выбранного, а не на кнопку
    закрытия: карточку окна снимают ещё и повторным нажатием по коробке,
    переходом к соседней и началом перетаскивания, и каждый из этих путей
    оставлял бы висеть окно, на которое записаться уже нельзя (второй 409)
  */
  useEffect(() => {
    if (!staleOpening.current) return;
    staleOpening.current = false;
    void reload();
  }, [selectedKey, reload]);

  /**
   * Занятие исчезло из недели, пока карточка была открыта: отменённое разовое
   * уезжает в корзину вместе с уроком, перенесённое меняет колонку. Карточку в
   * этом случае закрываем, а не оставляем висеть у чужой коробки.
   */
  useEffect(() => {
    if (data !== null && selectedKey !== null && selectedEntry === null && selectedOpening === null) {
      setSelected(null);
    }
  }, [data, selectedEntry, selectedOpening, selectedKey]);

  /**
   * Чат занятия: лента открытой карточки и точки непрочитанного в клетках.
   *
   * ⚠️ **Хук стоит ЗДЕСЬ, а не в карточке, и это требование раздела 12
   * `docs/room-chat-design.md`.** Карточка закрывается на любое нажатие мимо и
   * на Escape, то есть набранная и не отправленная фраза уносилась бы движением
   * мыши по соседней клетке. Живя выше карточки, черновик переживает и
   * закрытие, и переход к соседнему занятию.
   */
  const { chat, dots: chatDots } = useScheduleChat({
    entries,
    selected: selectedEntry,
    from,
    to,
    enabled: state.status === "signed",
  });

  /**
   * Минута отметки «сейчас» для календаря.
   *
   * `null`, если сегодняшнего дня среди колонок нет: в чужой неделе полосе
   * текущего времени стоять негде, а прижатая к краю она врала бы про день, на
   * который человек смотрит.
   */
  const nowMin = useMemo(
    () => (dates.includes(todayLocal) ? localMinutesAt(now, zone) : null),
    [dates, now, todayLocal, zone],
  );

  /** Порядковый номер жеста: им окно итога пересоздаётся, а не переиспользуется. */
  const dropSeq = useRef(0);
  const [drop, setDrop] = useState<{
    seq: number;
    entry: ScheduleEntry;
    plan: DropPlan;
  } | null>(null);

  /**
   * Перенос занятия перетаскиванием.
   *
   * ⚠️ **Жест включает РОЛЬ, а не устройство** (12.09.2026). До этого дня здесь
   * стояло ещё и `pointer: fine`, то есть на телефоне жеста не было вовсе:
   * сетка прокручивается и вниз, и вбок, а порог в четыре пикселя отобрал бы у
   * пальца прокрутку, то есть главный способ смотреть расписание. Довод снят
   * удержанием: палец берёт занятие полусекундой, и прокрутка остаётся при нём
   * (раздел 9 `docs/schedule-drag-design.md`). Клавиатурный путь по-прежнему
   * форма переноса в карточке, и он полноценный.
   */
  const drag = useMemo<CalendarDrag | null>(
    () =>
      canCreate
        ? {
            onStart: () => {
              setSelected(null);
            },
            onDrop: (entry, plan) => {
              // Исход 1: цель совпала с текущим местом. Ни запроса, ни полосы:
              // сказать тут нечего, ничего не произошло
              if (plan.action.kind === "none") return;
              dropSeq.current += 1;
              setDrop({ seq: dropSeq.current, entry, plan });
            },
          }
        : null,
    [canCreate],
  );

  /** Занятия видимых колонок: счётчик обязан считать то, что на экране. */
  const visible = useMemo(() => {
    const set = new Set(dates);
    return entries.filter((e) => set.has(localDateAt(e.at, zone)));
  }, [dates, entries, zone]);
  const count = visible.filter((e) => e.state !== "cancelled" && e.state !== "moved_away").length;
  /*
    Счётчик показывается только тогда, когда число не соврёт: пока данных нет,
    пока летит запрос и когда список обрезан потолком, считать нечего.

    Условие вынесено переменной просто ради читаемости разметки: признак
    `data-sub`, из которого `styles.css` рисует разделитель перед подзаголовком,
    стоит литералом ВНУТРИ этого же условия, поэтому разъехаться им нечем и
    висящей точки у пустого места не бывает по устройству.
  */
  const showCount = data !== null && !busy && !data.truncated;
  /*
    Половины подписи зоны: место и смещение. Порознь они нужны кнопке пояса на
    узком экране, где смещение стоит первым, а имя города обрезается (разбор у
    самой кнопки и в `lib/time-zone.ts`). Зона тут та же, что у всего экрана:
    ответ сервера, а пока он не пришёл, догадка браузера.
  */
  const zoneParts = zoneLabelParts(data?.timeZone ?? zone);

  return (
    // Расписание шире прочих разделов: семь колонок в 44rem это по 60 px на
    // день, и коробка занятия перестаёт вмещать время с именем.
    //
    // ⚠️ `data-fill` это «раздай мою высоту детям колонкой», и работает он в
    // паре с `data-fixed` у каркаса (`Shell.tsx`, там же причина). Высоту окна
    // даёт каркас по маршруту, а кто из детей растёт остатком, решает экран:
    // здесь это блок с календарём. Признак отдельный, потому что второй экран
    // каркаса, редактор заготовки, высоту раздаёт сам
    <main className="home home--wide" data-fill="true">
      {/*
        Отказ входа приезжает сюда параметром адреса вместе с переадресацией с
        корня, и показать его обязан тот экран, на который человек попал. При
        `google_other_user` сессия остаётся живой, кабинет открывается, и
        сообщение, живущее только на форме входа, никто никогда не увидит
      */}
      <AuthErrorBanner />
      {/*
        Окно смены пояса. Рисуется здесь, а не внутри строки «время в зоне …»:
        нативный `<dialog>` живёт в верхнем слое браузера, и место в разметке на
        вид не влияет, зато влияет на порядок обхода читалкой.
      */}
      {/*
        ⚠️ У неполной сессии вместо окна смены объяснение (9.6
        `docs/account-adopt-design.md`): пояс сдвинул бы время занятий и у тех
        преподавателей, чьих ссылок в этом браузере не открывали, и сервер
        ответит `session_partial`. Запроса нет.
      */}
      {changingZone && partial && (
        <PartialSessionNote title="Часовой пояс" onClose={() => setChangingZone(false)} />
      )}
      {changingZone && state.status === "signed" && !partial && (
        <TimeZoneDialog me={state.me} onDone={adopt} onClose={() => setChangingZone(false)} />
      )}
      <header className="head">
        <div className="head__line">
          <h1 className="head__h">Расписание</h1>
          {/*
            ⚠️ `data-sub` говорит ОДНО: за счётчиком на узком экране идёт
            подзаголовок, и между ними нужна точка-разделитель. Рисует её
            `styles.css`, потому что склеить «2 занятия» и «17 сентября» строкой
            значило бы завести третий текст рядом с `occurrenceCountText` и
            `rangeTitle`, и уехал бы он в чужие экраны первым же копированием.

            Признаком, а не `:has()`: цель сборки включает firefox115, где
            `:has()` нет, и правило браузер выбрасывает молча (замок
            `lib/css-targets.test.ts`). Экран без подзаголовка признака не
            ставит, и точка у него не появляется.
          */}
          {showCount && (
            <span className="head__n" data-sub="true">
              {occurrenceCountText(count)}
            </span>
          )}
          {/*
            Вход в серии стоит рядом с неделей, а не в сайдбаре: серия это
            редкое действие, и отдельный пункт разделов ради него означал бы
            шестой пункт, в который заходят три раза в год.

            Ученику этой кнопки нет: серии заводит и правит преподаватель, а
            смотреть в них ученику нечего, всё его расписание и так на экране

            ⚠️ **«Серии» это главное действие расписания, и потому оно
            залито.** Решение 14.09.2026 по артборду системы.

            Разобрано это дважды, поэтому довод записан целиком, чтобы не
            открывать в третий раз. Против заливки говорили так: расписание
            показывает, а не создаёт (занятие заводится нажатием по пустому
            месту календаря), и на самом экране серий залитая «Новая серия» уже
            есть, то есть два залитых подряд на одном пути обесценивают первое.

            Довод отвергнут по устройству правила, а не по вкусу. В слоте
            главного действия не бывает кнопки ТРЕТЬЕГО вида: либо она залита,
            либо места нет вовсе и кнопка живёт в другом месте экрана. Обычный
            вес в этом слоте это ровно одна из пяти сегодняшних трактовок
            главного действия, ради снятия которых слот и заводился. А два
            залитых подряд не спорят: они на РАЗНЫХ экранах, и у каждого экрана
            своё главное действие, в этом и состоит правило.
          */}
          {canCreate && (
            <span className="head__act">
              <Link className="btn btn--primary" to="/schedule/series">
                Серии
              </Link>
            </span>
          )}
        </div>
        <div className="head__sub">
          {rangeTitle(dates[0] ?? from, dates[dates.length - 1] ?? to)}
          {data !== null && !busy && data.truncated && " · показана часть занятий"}
        </div>
      </header>

      {err !== null && <p className="err">{err}</p>}

      <section className="card">
        {/*
          Листание недели это хром, а не действие: оно ничего не создаёт и
          ничего не меняет, оно двигает окно просмотра. Обычным весом эти три
          кнопки весили столько же, сколько «Завести серию» на соседнем экране,
          то есть читались как призыв нажать.

          ⚠️ **Сегментом эти три НЕ становятся**, хотя на артборде системы они
          нарисованы им (14.09.2026). Сегмент говорит «выбрано вот это из
          нескольких», а здесь выбирать нечего: «Раньше» и «Позже» сдвигают
          окно на шаг, «Сегодня» возвращает к сегодняшнему дню, и ни одно из
          трёх не остаётся отмеченным после нажатия. Отметить «Сегодня», когда
          открыта следующая неделя, значит соврать, а не отметить нельзя:
          радиогруппа без выбранного не бывает. Это три кнопки, и вид у них
          кнопочный.
        */}
        <div className="week__nav">
          {/*
            ⚠️ **«Раньше» и «Позже» это фигуры, а «Сегодня» слово, и разница не
            в длине подписи** (17.09.2026). Шеврон говорит «на шаг назад» сам, а
            «сегодня» фигурой не сказать: стрелка не знает, в какую сторону
            шагать до текущего дня, и любая картинка тут была бы ребусом.
            Правило проекта «значок вместо слова там, где фигура говорит то же»
            ровно про этот случай. Читалке и подсказке слова остались оба:
            `aria-label` плюс `title`, как у всех кнопочных значков продукта.

            ⚠️ **Тройка собрана в свою коробку**, и коробка держит два дела.
            Первое: при переносе полосы тройка едет целиком, а не оставляет
            «Позже» одну на следующей строке. Второе живёт в стилях: у
            квадратной кнопки область нажатия растёт и вбок
            (`.btn--ico::after`), поэтому на грубом указателе зазор внутри
            тройки шире зазора полосы, иначе палец у левой кромки «Сегодня»
            попадал бы в «Раньше».
          */}
          <span className="week__steps">
            <button
              type="button"
              className="btn btn--quiet btn--ico"
              aria-label="Раньше"
              title="Раньше"
              onClick={() => shift(-1)}
            >
              <ChevronIcon direction="left" />
            </button>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                /*
                  ⚠️ Кнопка делает два дела, и второе с 17.09.2026: `go` ставит
                  в адрес сегодняшнюю неделю и сегодняшний день, `ask` просит
                  окно встать на текущее время. Просьбу здесь никто не снимает
                  (листание гасит её в `shift`), поэтому порядок двух вызовов на
                  исход не влияет, и написан он просто по смыслу: сначала куда
                  смотрим, потом куда встаём.

                  До этого дня кнопка не делала ничего вовсе в одном случае:
                  когда показанное окно уже стоит на сегодняшнем дне, адрес от
                  неё не менялся, и перерисовывать было нечего. Случай этот не
                  редкий, он и есть умолчание захода. Теперь она возвращает к
                  текущему времени, и это её главная работа у того, кто
                  прокрутил день до вечера.
                */
                go({ week: mondayOf(todayLocal), day: todayLocal });
                askNowScroll();
              }}
            >
              Сегодня
            </button>
            <button
              type="button"
              className="btn btn--quiet btn--ico"
              aria-label="Позже"
              title="Позже"
              onClick={() => shift(1)}
            >
              <ChevronIcon direction="right" />
            </button>
          </span>

          {/*
            Переключатель режима, а не автоподбор по ширине: семь колонок в 360
            пикселей не встают, но выбор всё равно за человеком. На телефоне
            умолчание это один день, и оно считается один раз при заходе.

            ⚠️ **Сегмент, а не три кнопки с `data-on`** (14.09.2026, подход 2е
            переноса дизайн-системы). Выбран ровно один режим из трёх, значит
            это сегмент; три кнопки подряд были первым из пяти видов
            переключателя, и читалке они объявляли три независимых действия там,
            где выбор один. Рядом при этом стоят три обычные кнопки листания, и
            теперь их видно: они ДЕЙСТВИЯ, а не режимы.

            День НЕ передаётся, и это важно: в недельном режиме якорь всегда
            прижат к понедельнику, и переключение «неделя → день» с ним показало
            бы понедельник вместо сегодняшнего дня. Оставленный параметр адреса
            сам даст либо выбранный день, либо сегодня.
          */}
          <SegmentedRadio
            ariaLabel="Сколько дней показывать"
            segments={[
              { id: "day", label: "День" },
              { id: "days3", label: "3 дня" },
              { id: "week", label: "Неделя" },
            ]}
            value={view}
            onPick={(id) => go({ view: id as View })}
          />

          {/*
            Зона показана всегда, а не только при расхождении: время занятия
            без указания, в чьих часах оно написано, это ровно та подпись, из-за
            которой люди приходят на час позже.

            ⚠️ **Здесь же единственная дверь к смене пояса** (20.08.2026). До
            этого сменить его было НЕЛЬЗЯ НИЧЕМ, кроме случая, когда часы
            браузера начинали спорить с записанным: подтвердивший зону под VPN
            и не выключивший VPN оставался с неверным временем каждого занятия
            навсегда. Дверь стоит именно здесь, а не в подвале сайдбара, потому
            что пояс не влияет ни на что, кроме этих самых времён, и чинить его
            человек приходит, глядя на них.

            ⚠️ **Расхождение с часами устройства НАЗВАНО, но не спрошено.**
            Раньше на этом месте продукт задавал вопрос «обновить?», и под него
            была написана память об отказе с окном в сутки, иначе человек под
            VPN отбивался бы от вопроса каждый день. Отличить переезд от VPN
            браузер не умеет, значит любой ответ это наша догадка, которую мы
            просим подтвердить человека. У факта же неверного ответа нет, и
            отбиваться от него не нужно: он просто лежит рядом с «Изменить».
          */}
          {/*
            Имя зоны показано по-русски («Москва (GMT+3)»), а имя IANA осталось в
            подсказке. Локализация экранная: на проводе и в базе по-прежнему
            `Europe/Moscow`, подпись строится из идентификатора, а не заменяет
            его. Разбор в `lib/time-zone.ts`.
          */}
          {/*
            ⚠️ **Подпись и дверь это ОДИН узел** (17.09.2026). До этого дня
            рядом стояли текст «время в зоне …» и отдельная кнопка «Изменить», и
            на телефоне эта пара занимала целый ряд полосы, то есть полчаса
            календаря на экране, который не прокручивается. Теперь кнопка одна и
            она же подпись: на узком от неё остаётся короткое имя пояса, а слова
            «время в зоне» и «Изменить» гасит `styles.css`.

            ⚠️ **Гасит ОТЪЕЗДОМ ЗА КРАЙ, а не `display: none`**, и это не
            перестраховка: имя кнопки читалка собирает из её содержимого, и
            спрятанные насовсем слова забрали бы с собой и глагол. «Австрия
            (GMT+2), кнопка» без глагола это ребус; с отъездом читалка
            по-прежнему слышит «время в зоне Австрия (GMT+2) Изменить» на любой
            ширине. Поэтому же тут нет `aria-label`: он был бы вторым текстом
            рядом с теми же словами.

            ⚠️ **Цена названа: на широком нажимаемой стала вся фраза, а не одно
            слово «Изменить».** Слова и цвета те же, места сдвинулись на глаз
            незаметно (коробка 1128,6 против 1130,5, сама фраза уехала вправо
            примерно на 7 px, «Изменить» на 2,4), но наведение теперь
            подсвечивает строку целиком. Это плата за один узел вместо двух;
            второй узел означал бы две остановки клавиатуры, ведущие в одно и
            то же окно.

            ⚠️ **Половины подписи стоят отдельными узлами** (`zoneLabelParts`),
            и нужны они одному месту: на узком смещение показывается ПЕРВЫМ, а
            имя города обрезается многоточием («GMT+12, Петропавловск-Кам…»).
            Иначе длинные российские зоны не влезают в строку рядом с сегментом
            режимов, и полоса остаётся трёхрядной, то есть выигрыша нет вовсе.
            Обрезается именно город: смещение это то единственное, по чему
            человек ловит расхождение со своими часами.

            ⚠️ Полное имя города остаётся ровно в ОДНОМ месте, в окне смены
            пояса. Подсказкой при наведении оно не закрывается: обрезается город
            только на узком, то есть на телефоне, а наведения там не бывает
            вовсе.

            Порядок и знаки препинания делают стили, а не вторая склейка:
            подпись зоны собирается в `zoneLabel`, и только там.
          */}
          <span className="muted week__zone">
            <button
              type="button"
              className="btn btn--quiet week__zone-btn"
              aria-disabled={partial ? true : undefined}
              onClick={() => setChangingZone(true)}
            >
              <span className="week__zone-pre">время в зоне</span>
              <span className="week__zone-name">
                {/*
                  ⚠️ Имя IANA живёт подсказкой на ГОРОДЕ, а не на кнопке, и это
                  не вкусовщина. Подсказка на самом контроле уезжает читалке
                  описанием, то есть после имени кнопки она диктовала бы
                  «Europe/Moscow» вслух. На неинтерактивном узле она остаётся
                  тем, чем была до 17.09.2026: сноской для того, кто наводит
                  мышь.
                */}
                <span className="week__zone-city" title={data?.timeZone ?? zone}>
                  {zoneParts.name}
                </span>
                {zoneParts.offset !== "" && (
                  <span className="week__zone-off">{zoneParts.offset}</span>
                )}
              </span>
              <span className="week__zone-edit">Изменить</span>
            </button>
          </span>
          {/*
            ⚠️ Расхождение с часами устройства стоит СНАРУЖИ кнопки и на узком
            занимает свою строку, но только когда оно есть. Внутрь его убирать
            нельзя дважды: в имени кнопки оно превратилось бы в часть действия,
            а в коробке `--h-ctl` оно попросту не помещается.

            ⚠️ **И снаружи САМОЙ строки пояса, а не только кнопки** (17.09.2026):
            на узком строка ужимается до остатка ряда, и предупреждение,
            лежащее внутри неё, переносилось бы по два-три слова в колонке
            шириной с кнопку. Здесь оно ребёнок полосы и берёт всю её ширину.

            ⚠️ Из-за этого на широком оно переехало за слово «Изменить», а
            стояло перед ним. Это единственное, что в широкой строке
            сдвинулось.
          */}
          {drifted && (
            <span className="muted week__zone-drift" title={browserZone}>
              (часы устройства показывают {zoneLabel(browserZone)})
            </span>
          )}
        </div>

        {/*
          Подсказка и сетка стоят РЯДОМ, а не одна над другой.

          Ни над сеткой, ни под ней ей места нет: под сеткой её не видит никто
          (неделя это семь колонок высотой в сутки, текст под ней лежит за
          нижней кромкой экрана), а над сеткой она отодвигает вниз календарь, по
          которому занятие как раз и заводится. Оверлеем тоже нельзя: карточку
          не сделать одновременно кликабельной (в ней ссылки) и прозрачной для
          нажатия по слоту под ней.

          Поэтому колонка сбоку. Она забирает ширину, а не высоту, и сетка не
          теряет ни одного нажимаемого слота. Ниже 82rem места сбоку физически
          нет, и та же разметка сворачивается в строку-цепочку над сеткой:
          развод видов целиком в CSS, разбор в `FirstSteps.tsx`.
        */}
        <div className="week__body" ref={gridRef} data-hint={weekIsEmpty && emptyCabinet}>
          {weekIsEmpty && emptyCabinet && <FirstSteps />}

          {data === null ? (
            // Пусто, если и данных нет, и загрузка кончилась: значит был отказ,
            // и он уже написан красной строкой выше
            busy ? (
              <Loading />
            ) : null
          ) : (
            <CalendarGrid
              entries={entries}
              dates={dates}
              zone={zone}
              today={todayLocal}
              nowMin={nowMin}
              chatDots={chatDots}
              selectedKey={selectedKey}
              onSelect={(e: ScheduleEntry, anchor: HTMLElement) =>
                setSelected((prev) => (prev?.key === e.key ? null : { key: e.key, anchor }))
              }
              onEmpty={canCreate ? (date, time) => setCreating({ date, time }) : null}
              drag={drag}
              openings={openings}
              onSelectOpening={(o: GridOpening, anchor: HTMLElement) =>
                setSelected((prev) => (prev?.key === o.key ? null : { key: o.key, anchor }))
              }
            />
          )}
        </div>

        {/*
          Итог жеста это всплывающая полоса внизу экрана, а не красная строка над
          сеткой: та лежит выше листалки недели, и человек, работающий с
          вечерними часами, прокручен вниз на семьсот пикселей и не увидит её
          вовсе. Окно у точки отпускания, стоявшее здесь до 10.09.2026, снято
          решением владельца: оно читалось как подтверждение уже сделанного.
          Разбор в `DropToast`
        */}
        {drop !== null && (
          <DropToast
            key={drop.seq}
            entry={drop.entry}
            landed={entries.find((e) => e.key === drop.entry.key) ?? null}
            plan={drop.plan}
            zone={zone}
            onChanged={reload}
            onClose={() => setDrop(null)}
          />
        )}

        {selected !== null && selectedEntry !== null && (
          <EntryPopover
            label={`Занятие ${timeText(selectedEntry.at, zone)}`}
            contentKey={selectedEntry.key}
            revision={selectedEntry}
            anchor={selected.anchor}
            onClose={() => setSelected(null)}
          >
            <EntryCard
              entry={selectedEntry}
              zone={zone}
              onChanged={reload}
              onClose={() => setSelected(null)}
              chat={<EntryChat chat={chat} role={selectedEntry.myRole} />}
            />
          </EntryPopover>
        )}

        {selected !== null && selectedOpening !== null && (
          <EntryPopover
            label={`${selectedOpening.mine ? "Открыто для записи" : "Свободное время"} ${timeText(selectedOpening.at, zone)}`}
            contentKey={selectedOpening.key}
            revision={selectedOpening}
            anchor={selected.anchor}
            onClose={closeOpeningCard}
          >
            <OpeningCard
              opening={selectedOpening}
              zone={zone}
              onChanged={reload}
              onStale={() => {
                staleOpening.current = true;
              }}
              onClose={closeOpeningCard}
            />
          </EntryPopover>
        )}

        {/*
          Пустых недель две, и подсказка у них разная. У преподавателя с
          учениками это «поставьте занятие», у преподавателя без единого ученика
          это «кабинет ещё не заведён», и первые шаги он уже прочитал рядом с
          сеткой: повторять ему ещё и это значит писать дважды об одном
        */}
        {noEntries && !emptyCabinet && (
          <p className="muted">
            {canCreate ? (
              <>
                Занятий на этой неделе нет. Нажмите на пустое время в календаре, чтобы завести
                занятие: разовое либо повторяющееся каждую неделю. Уже заведённые повторения живут в{" "}
                <Link className="textlink" to="/schedule/series">
                  сериях
                </Link>
                .{canBookHere && ` ${BOOK_HINT}`}
              </>
            ) : (
              // Ученику без окон предлагать нечего: расписание ведёт
              // преподаватель. С окнами есть что: записаться (`openings.ts`)
              studentEmptyWeekText(canBookHere)
            )}
          </p>
        )}
      </section>

      {creating !== null && (
        <CreateOccurrenceDialog
          date={creating.date}
          time={creating.time}
          zone={zone}
          onClose={() => {
            setCreating(null);
            // Ученика заводят и здесь тоже, поэтому «кабинет пуст» перепроверяем
            // на закрытии, а не только на успешном создании занятия
            recheckCabinet();
          }}
          onCreated={reload}
          canOpen
        />
      )}
    </main>
  );
}
